import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Calculator, Plus, X, Download, Loader2, ArrowLeft, Eye, EyeOff, ChevronDown, Save, RotateCw, Search } from 'lucide-react';
import { signageAPI, customerAPI, branchAPI, quotationAPI, authAPI, salespersonAPI, productAPI } from '../../services/api';
import { filterBranchesForUser } from '../../utils/branchScope';
import CustomerSearchInput from '../ui/CustomerSearchInput';
import { generateSignagePDF as generateSignagePDFUtil } from './signagePdfUtils';
import SignageBoardItem from './SignageBoardItem';
import SignageCostSummary from './SignageCostSummary';
import { computeAreaSqm } from './signageSqmUtils';

const SHAPES = ['Rectangular', 'Circular', 'Triangular', 'Hazard', 'Octagonal', 'Gantry'];
const WORK_FOR_OPTIONS = ['Supply Only', 'Supply & Installation', 'Installation Only'];

const BOARD_DESCRIPTIONS = [
  'Cautionary Sign', 'Mandatory Sign', 'Informatory Sign', 'Warning Sign',
  'Guide Sign', 'Gantry Board', 'Overhead Sign', 'Chevron Marker',
  'Kilometre Stone', 'Direction Sign', 'Toll Plaza Sign', 'Speed Limit Sign',
  'Highway Marker', 'Delineator', 'Other',
];

const defaultTerms = {
  payment_terms: '50% advance, 50% against delivery',
  delivery_schedule: 'Within 15 days from PO receipt',
  warranty: '12 months from date of application',
  validity: '30 days from quotation date',
  freight: 'FOR Destination (included in price)',
  notes: 'Rates are exclusive of GST. Material as per IRC/MoRTH specifications.',
};

const freightOptions = [
  'FOR Destination (included in price)',
  'FOR Destination (extra at actual)',
  'Ex-Works (buyer to arrange pickup)',
  'Paid by Prismoline at actual',
  'To Pay (freight paid by buyer)',
];

const CONCRETE_GRADES = [
  { name: 'M15', rate: 4500 },
  { name: 'M20', rate: 5500 },
  { name: 'M25', rate: 6500 },
];
const DEFAULT_REBAR_RATE = 75;

const emptyItem = {
  shape: 'Rectangular', description: '', length_mm: '', height_mm: '', diameter_mm: '', qty: 1,
  base_material: '', retro_sheet: '', work_type: '',
  base_material_rate: '', retro_sheet_rate: '', work_type_rate: '', labour_rate: '',
  post_type: '', post_length_ft: '', num_posts: '', post_weight_override: '',
  frame_type: '', frame_support_length_ft: '', frame_weight_override: '',
  laminate_sku: '', sqft_rate_override: '',
  pricing_mode: 'sqft', custom_per_pcs_rate: '',
  specific_description: '',
  disc_type: 'amount', disc_value: '',
  // Foundation
  foundation_enabled: false, concrete_grade: '', pit_length_m: '', pit_width_m: '', pit_depth_m: '',
  num_pits: '', concrete_rate: '', rebar_kg: '', rebar_rate: DEFAULT_REBAR_RATE,
};

const SignageQuotationBuilder = ({ onBack, editQuotation }) => {
  const [customers, setCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [rateCard, setRateCard] = useState(null);
  const [laminates, setLaminates] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);  // Sync double-click guard (prevents duplicate quotes)
  const [calculating, setCalculating] = useState(false);
  const [showInternalCosts, setShowInternalCosts] = useState(true);
  const [calcResult, setCalcResult] = useState(null);
  const [needsRecalc, setNeedsRecalc] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState(''); // '', 'saving', 'saved'
  const [draftId, setDraftId] = useState(editQuotation?.id || '');
  const [logoBase64, setLogoBase64] = useState('');
  const [customerShipAddresses, setCustomerShipAddresses] = useState([]);
  const isEdit = !!editQuotation;
  const user = JSON.parse(localStorage.getItem('prismoline_user') || '{}');

  const buildInitialForm = () => {
    if (editQuotation) {
      const eq = editQuotation;
      const sp = eq.signage_project || {};
      return {
        customer: eq.customer || '', customer_name: eq.customer_name || '',
        customer_gstin: eq.customer_gstin || '', customer_address: eq.customer_address || '',
        customer_city: eq.customer_city || '', customer_state: eq.customer_state || '',
        customer_phone: eq.customer_phone || '',
        salesman: eq.salesman || user.id || '', salesman_name: eq.salesman_name || user.name || '',
        salesman_phone: eq.salesman_phone || '',
        branch: eq.branch || user.branch || '',
        ship_to_name: eq.ship_to_name || '', ship_to_address: eq.ship_to_address || '',
        ship_to_city: eq.ship_to_city || '', ship_to_state: eq.ship_to_state || '',
        valid_until: eq.valid_until || '',
        gst_rate: eq.gst_rate || 18,
        project_name: sp.project_name || '', stretch_no: sp.stretch_no || '',
        work_for: sp.work_for || '',
        work_type: sp.work_type || '',
        margin_percent: sp.margin_percent || 0, overhead_percent: sp.overhead_percent || 0, transportation_amount: sp.transportation_amount || 0,
        post_per_kg_rate: '', frame_per_kg_rate: '', installation_per_pcs: '',
        items: (eq.signage_items || []).map(si => ({
          shape: si.shape || 'Rectangular', description: si.description || '',
          length_mm: si.length_mm || '', height_mm: si.height_mm || '', diameter_mm: si.diameter_mm || '',
          qty: si.qty || 1, base_material: si.base_material || '', retro_sheet: si.retro_sheet || '',
          work_type: si.work_type || '',
          base_material_rate: '', retro_sheet_rate: '', work_type_rate: '', labour_rate: '',
          post_type: si.post_type || '',
          post_length_ft: si.post_length_ft || '', num_posts: si.num_posts || '',
          post_weight_override: '',
          frame_type: si.frame_type || '', frame_support_length_ft: si.frame_support_length_ft || '',
          frame_weight_override: '',
          laminate_sku: si.laminate_sku || '', sqft_rate_override: '',
          pricing_mode: si.pricing_mode || 'sqft', custom_per_pcs_rate: si.custom_per_pcs_rate || '', custom_sqm_rate: si.custom_sqm_rate || '',
          specific_description: si.specific_description || '',
          disc_type: si.disc_type || 'amount', disc_value: si.disc_value || '',
        })),
        terms: eq.terms || { ...defaultTerms },
        discount_type: eq.discount_type || 'amount', discount_value: eq.discount_value || '',
        normal_items: (eq.items || []).map(it => ({
          product: it.product || '', name: it.name || '', description: it.description || '',
          hsn_code: it.hsn_code || '', qty: it.qty || 1, unit: it.unit || 'PCS',
          rate: it.rate || 0, amount: it.amount || 0,
        })),
      };
    }
    return {
      customer: '', customer_name: '',
      customer_gstin: '', customer_address: '', customer_city: '', customer_state: '', customer_phone: '',
      salesman: user.id || '', salesman_name: user.name || '',
      salesman_phone: '',
      branch: user.branch || '',
      ship_to_name: '', ship_to_address: '', ship_to_city: '', ship_to_state: '',
      valid_until: '',
      gst_rate: 18,
      project_name: '', stretch_no: '',
      work_for: '',
      work_type: '',
      margin_percent: 0, overhead_percent: 0, transportation_amount: 0,
      post_per_kg_rate: '', frame_per_kg_rate: '', installation_per_pcs: '',
      items: [{ ...emptyItem }],
      terms: { ...defaultTerms },
      discount_type: 'amount', discount_value: '',
      normal_items: [],
    };
  };

  const [form, setForm] = useState(buildInitialForm);

  // When editing, reconstruct calcResult from saved signage_items so totals display and buttons enable
  useEffect(() => {
    if (isEdit && editQuotation?.signage_items?.length > 0 && !calcResult) {
      const savedItems = editQuotation.signage_items.map(si => ({
        shape: si.shape || '', dimensions: si.dimensions || '',
        qty: si.qty || 0, area_sqft: si.area_sqft || 0,
        facia_rate: si.facia_rate || 0, post_rate: si.post_rate || 0,
        post_weight_kg: si.post_weight_kg || 0, frame_rate: si.frame_rate || 0,
        frame_weight_kg: si.frame_weight_kg || 0, board_rate: si.board_rate || 0,
        installation_rate: si.installation_rate || 0,
        cost_per_sqft: si.area_sqft > 0 ? Math.round(si.final_per_board / si.area_sqft) : 0,
        final_per_board: si.final_per_board || 0, total_amount: si.total_amount || 0,
        is_override: false,
        laminate_sku: si.laminate_sku || '', laminate_name: si.laminate_name || '',
        laminate_per_sqft: si.laminate_per_sqft || 0,
        foundation_cost: si.foundation_cost || 0, concrete_volume_m3: si.concrete_volume_m3 || 0,
        concrete_cost: si.concrete_cost || 0, rebar_cost: si.rebar_cost || 0,
      }));
      const itemsTotal = savedItems.reduce((s, i) => s + (i.total_amount || 0), 0);
      setCalcResult({
        items: savedItems,
        total_facia: savedItems.reduce((s, i) => s + (i.facia_rate || 0), 0),
        total_fabrication: savedItems.reduce((s, i) => s + (i.post_rate || 0) + (i.frame_rate || 0), 0),
        total_installation: savedItems.reduce((s, i) => s + (i.installation_rate || 0), 0),
        total_foundation: savedItems.reduce((s, i) => s + (i.foundation_cost || 0), 0),
        items_total: itemsTotal,
        grand_total: editQuotation.total || 0,
      });
    }
  }, [isEdit, editQuotation]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [customersData, branchesData, rateCardData, laminatesData, usersData, spData, productsData] = await Promise.all([
        customerAPI.getAll().catch(() => []),
        branchAPI.getAll().catch(() => []),
        signageAPI.getRateCard().catch(() => null),
        signageAPI.getLaminates().catch(() => []),
        authAPI.listUsers().catch(() => []),
        salespersonAPI.getAll().catch(() => []),
        productAPI.getAll({ page: 1, page_size: 500 }).catch(() => []),
      ]);
      setCustomers(customersData);
      setBranches(branchesData);
      setLaminates(laminatesData);
      setUsers(usersData);
      setSalespersons(spData);
      setProducts(Array.isArray(productsData) ? productsData : (productsData?.items || []));
      // Auto-fill salesman_phone for existing quotations that don't have it saved
      if (isEdit && !form.salesman_phone && form.salesman && spData.length > 0) {
        const sp = spData.find(x => x.id === form.salesman);
        if (sp?.phone) {
          setForm(prev => ({ ...prev, salesman_phone: sp.phone }));
        }
      }
      if (rateCardData) {
        setRateCard(rateCardData);
        // Don't auto-fill margin/overhead from rate card — user wants 0 as default
      }
      // Fetch shipping addresses for the customer (if editing)
      if (isEdit && editQuotation?.customer) {
        try {
          const addrs = await customerAPI.getShippingAddresses(editQuotation.customer);
          setCustomerShipAddresses(Array.isArray(addrs) ? addrs : []);
        } catch { setCustomerShipAddresses([]); }
      }
    } catch (err) { console.error('Error:', err); }
    finally { setLoading(false); }
  }, [isEdit]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Preload company logo for PDF generation
  useEffect(() => {
    fetch('/prismoline-logo.png')
      .then(r => r.blob())
      .then(blob => { const reader = new FileReader(); reader.onloadend = () => setLogoBase64(reader.result); reader.readAsDataURL(blob); })
      .catch(() => {});
  }, []);

  // Auto-save draft logic
  // SAFETY: Auto-save NEVER creates a new quotation — it only UPDATES an existing draft
  // (one that the user has explicitly saved or is editing). This prevents phantom ₹0 blank
  // quotations from being created when the user opens the form and navigates away without saving.
  const autoSaveDraft = useCallback(async () => {
    // Only auto-save if we already have a saved draft to update (prevents phantom records)
    if (!draftId) return;
    // Only auto-save if there's meaningful data
    const hasData = form.customer && form.items.some(i => i.length_mm || i.diameter_mm || i.description);
    if (!hasData || submitting) return;

    try {
      setAutoSaveStatus('saving');
      const autoAdj = getAdjustedTotals();
      const signageItems = calcResult ? calcResult.items.map((r, i) => {
        const formItem = form.items[i] || {};
        const isPcs = formItem.pricing_mode === 'per_pcs';
        const isSqm = formItem.pricing_mode === 'sqm' && (r.shape || formItem.shape) === 'Rectangular';
        const pi = autoAdj.perItem[i] || { adjustedRate: 0, net: 0, discType: 'amount', discValue: 0, discAmt: 0 };
        return {
          shape: r.shape, dimensions: r.dimensions, description: formItem.description || '', qty: r.qty, area_sqft: r.area_sqft,
          area_sqm: isSqm ? computeAreaSqm({ shape: r.shape, length_mm: formItem.length_mm, height_mm: formItem.height_mm }) : 0,
          length_mm: Number(formItem.length_mm) || 0, height_mm: Number(formItem.height_mm) || 0, diameter_mm: Number(formItem.diameter_mm) || 0,
          base_material: formItem.base_material || '', retro_sheet: formItem.retro_sheet || '', work_type: formItem.work_type || form.work_type || '',
          post_type: formItem.post_type || '', post_length_ft: Number(formItem.post_length_ft) || 0, num_posts: Number(formItem.num_posts) || 0,
          frame_type: formItem.frame_type || '', laminate_sku: r.laminate_sku || '', laminate_name: r.laminate_name || '', laminate_per_sqft: r.laminate_per_sqft || 0,
          facia_rate: r.facia_rate, post_rate: r.post_rate, post_weight_kg: r.post_weight_kg || 0, frame_rate: r.frame_rate, frame_weight_kg: r.frame_weight_kg || 0, board_rate: r.board_rate, installation_rate: r.installation_rate,
          final_per_board: pi.adjustedRate, total_amount: pi.net,
          disc_type: pi.discType, disc_value: pi.discValue, discount_amount: pi.discAmt,
          pricing_mode: isSqm ? 'sqm' : (isPcs ? 'per_pcs' : 'sqft'), custom_per_pcs_rate: isPcs ? pi.adjustedRate : 0, custom_sqm_rate: isSqm ? (Number(formItem.custom_sqm_rate) || 0) : 0,
          specific_description: formItem.specific_description || '',
          unit: isSqm ? 'SQM' : 'NOS',
        };
      }) : form.items.map(fi => ({
        shape: fi.shape || 'Rectangular', dimensions: '', description: fi.description || '', qty: Number(fi.qty) || 1, area_sqft: 0,
        length_mm: Number(fi.length_mm) || 0, height_mm: Number(fi.height_mm) || 0, diameter_mm: Number(fi.diameter_mm) || 0,
        base_material: fi.base_material || '', retro_sheet: fi.retro_sheet || '', work_type: fi.work_type || form.work_type || '',
        post_type: fi.post_type || '', post_length_ft: Number(fi.post_length_ft) || 0, num_posts: Number(fi.num_posts) || 0,
        frame_type: fi.frame_type || '', laminate_sku: '', laminate_name: '', laminate_per_sqft: 0,
        facia_rate: 0, post_rate: 0, post_weight_kg: 0, frame_rate: 0, frame_weight_kg: 0, board_rate: 0, installation_rate: 0,
        final_per_board: 0, total_amount: 0, pricing_mode: fi.pricing_mode || 'sqft', custom_per_pcs_rate: Number(fi.custom_per_pcs_rate) || 0, custom_sqm_rate: Number(fi.custom_sqm_rate) || 0,
        disc_type: fi.disc_type || 'amount', disc_value: Number(fi.disc_value) || 0, discount_amount: 0,
        unit: 'NOS',
      }));

      const payload = {
        customer: form.customer || '', customer_name: form.customer_name || '',
        customer_gstin: form.customer_gstin || '', customer_address: form.customer_address || '',
        customer_city: form.customer_city || '', customer_state: form.customer_state || '',
        customer_phone: form.customer_phone || '',
        salesman: form.salesman || '', salesman_name: form.salesman_name || '',
        salesman_phone: form.salesman_phone || '',
        branch: form.branch || '', quotation_type: 'signage',
        items: (form.normal_items || []).filter(it => (it.name || '').trim()).map(it => ({
          product: it.product || '', name: (it.name || '').trim(), description: it.description || '',
          hsn_code: it.hsn_code || '', qty: Number(it.qty) || 0, unit: it.unit || 'PCS',
          rate: Number(it.rate) || 0, discount: 0,
          amount: Math.round((Number(it.qty) || 0) * (Number(it.rate) || 0) * 100) / 100,
        })),
        ship_to_name: form.ship_to_name || '', ship_to_address: form.ship_to_address || '',
        ship_to_city: form.ship_to_city || '', ship_to_state: form.ship_to_state || '',
        signage_items: signageItems,
        signage_project: {
          project_name: form.project_name || '', stretch_no: form.stretch_no || '',
          work_for: form.work_for || '', work_type: form.work_type || '',
          margin_percent: Number(form.margin_percent) || 0, overhead_percent: Number(form.overhead_percent) || 0,
          transportation_amount: Number(form.transportation_amount) || 0,
          post_per_kg_rate: Number(form.post_per_kg_rate) || 0,
          frame_per_kg_rate: Number(form.frame_per_kg_rate) || 0, installation_per_pcs: Number(form.installation_per_pcs) || 0,
        },
        subtotal: 0, gst_rate: form.gst_rate || 18, gst_amount: 0, total: 0,
        net_total: 0, discount_type: form.discount_type || 'amount', discount_value: Number(form.discount_value) || 0, discount_amount: 0,
        terms: form.terms || {}, valid_until: form.valid_until || '', status: 'Draft',
      };

      // Use calculated totals if available
      if (autoAdj.total > 0) {
        payload.subtotal = autoAdj.subtotal;
        payload.gst_amount = autoAdj.gst_amount;
        payload.total = autoAdj.total;
        payload.net_total = autoAdj.netTotal;
        payload.discount_amount = autoAdj.overallDiscAmt;
      }

      if (draftId) {
        await quotationAPI.update(draftId, payload);
      } else {
        // Defensive: should never reach here due to early return above, but never create from auto-save
        return;
      }
      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus(''), 3000);
    } catch (err) {
      console.error('Auto-save failed:', err);
      setAutoSaveStatus('');
    }
  }, [form, calcResult, draftId, submitting]);

  // Periodic auto-save every 30 seconds (only updates an existing draft — never creates)
  useEffect(() => {
    if (!draftId) return; // Skip the interval entirely until we have a draft to update
    const interval = setInterval(() => { autoSaveDraft(); }, 30000);
    return () => clearInterval(interval);
  }, [autoSaveDraft, draftId]);

  // Auto-save on page leave (beforeunload) — only updates an existing draft
  useEffect(() => {
    if (!draftId) return;
    const handleBeforeUnload = () => { autoSaveDraft(); };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [autoSaveDraft, draftId]);

  // Back button: DO NOT auto-save. If user navigates back without clicking Save, nothing is persisted.
  // This prevents phantom blank ₹0 records from being created.
  const handleBack = () => {
    onBack();
  };

  const selectCustomer = (customerId) => {
    const c = customers.find(x => x.id === customerId);
    if (c) setForm(prev => ({
      ...prev, customer: c.id, customer_name: c.name,
      customer_gstin: c.gstin || '', customer_address: c.address || '',
      customer_city: c.city || '', customer_state: c.state || '',
      customer_phone: c.phone || '',
    }));
  };

  // Lookup rate from rate card for a given material/retro/work type
  const getRateFromCard = (category, name) => {
    if (!rateCard || !name) return '';
    const list = category === 'base' ? rateCard.base_materials : category === 'retro' ? rateCard.retro_sheets : rateCard.work_types;
    const found = (list || []).find(m => m.name === name);
    return found ? found.rate : '';
  };

  const updateItem = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: value };
    // When material selection changes, clear the rate override so rate card value is used
    if (field === 'base_material') {
      items[idx].base_material_rate = '';
    } else if (field === 'retro_sheet') {
      items[idx].retro_sheet_rate = '';
    } else if (field === 'work_type') {
      items[idx].work_type_rate = '';
    }
    // Auto-sync num_pits with num_posts (installation = per post, foundation = per post)
    if (field === 'num_posts') {
      items[idx].num_pits = value;
    }
    // When enabling foundation, default num_pits to num_posts
    if (field === 'foundation_enabled' && value === true) {
      items[idx].num_pits = items[idx].num_posts || '';
    }
    // "Per Sq.m" mode is only valid for Rectangular boards — reset if shape changes away.
    if (field === 'shape' && value !== 'Rectangular' && items[idx].pricing_mode === 'sqm') {
      items[idx].pricing_mode = 'sqft';
    }
    setForm(prev => ({ ...prev, items }));
    // Mark as needing recalculation for pricing-affecting fields (but don't clear calcResult)
    if (field !== 'pricing_mode' && field !== 'custom_per_pcs_rate' && field !== 'specific_description' && field !== 'description') {
      setNeedsRecalc(true);
    }
  };

  const addItem = () => {
    setForm(prev => ({ ...prev, items: [...prev.items, { ...emptyItem }] }));
    setNeedsRecalc(true);
  };

  const removeItem = (idx) => {
    if (form.items.length <= 1) return;
    setForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
    setNeedsRecalc(true);
  };

  const handleCalculate = async () => {
    // Validate mandatory fields
    if (!form.work_for) { alert('Please select "Work For" before calculating'); return; }
    // Allow empty header work_type if every item has its own
    const allItemsHaveWorkType = form.items.length > 0 && form.items.every(i => !!i.work_type);
    if (!form.work_type && !allItemsHaveWorkType) { alert('Please select "Type of Work" (or set it per item on every row) before calculating'); return; }
    if (!form.branch) { alert('Please select a Branch before calculating'); return; }

    const validItems = form.items.filter(i => i.qty > 0 && (
      (i.shape === 'Rectangular' || i.shape === 'Gantry' || i.shape === 'Hazard') ? (i.length_mm > 0 && i.height_mm > 0) : i.diameter_mm > 0
    ));
    if (validItems.length === 0) { alert('Add at least one item with dimensions'); return; }

    try {
      setCalculating(true);
      const payload = {
        work_for: form.work_for || '',
        margin_percent: Number(form.margin_percent) >= 0 ? Number(form.margin_percent) : -1,
        overhead_percent: Number(form.overhead_percent) || 0,
        transportation_amount: Number(form.transportation_amount) || 0,
        // Global rate overrides — only send if user explicitly provided, else 0 = use rate card
        post_per_kg_rate: Number(form.post_per_kg_rate) || 0,
        frame_per_kg_rate: Number(form.frame_per_kg_rate) || 0,
        installation_per_pcs: Number(form.installation_per_pcs) || 0,
        items: validItems.map(i => ({
          shape: i.shape,
          length_mm: Number(i.length_mm) || 0,
          height_mm: Number(i.height_mm) || 0,
          diameter_mm: Number(i.diameter_mm) || 0,
          qty: Number(i.qty) || 1,
          base_material: i.base_material,
          retro_sheet: i.retro_sheet,
          work_type: i.work_type || form.work_type,
          // Per-item rate overrides — 0 means "use rate card value"
          base_material_rate_override: Number(i.base_material_rate) || 0,
          retro_sheet_rate_override: Number(i.retro_sheet_rate) || 0,
          work_type_rate_override: Number(i.work_type_rate) || 0,
          labour_rate_override: Number(i.labour_rate) || 0,
          post_type: i.post_type,
          post_length_ft: Number(i.post_length_ft) || 0,
          num_posts: Number(i.num_posts) || 0,
          post_weight_override: Number(i.post_weight_override) || 0,
          frame_type: i.frame_type,
          frame_support_length_ft: Number(i.frame_support_length_ft) || 0,
          frame_weight_override: Number(i.frame_weight_override) || 0,
          foundation_enabled: showFoundation && i.foundation_enabled,
          concrete_grade: i.concrete_grade || '',
          pit_length_m: Number(i.pit_length_m) || 0,
          pit_width_m: Number(i.pit_width_m) || 0,
          pit_depth_m: Number(i.pit_depth_m) || 0,
          num_pits: Number(i.num_pits) || 0,
          concrete_rate: Number(i.concrete_rate) || 0,
          rebar_kg: Number(i.rebar_kg) || 0,
          rebar_rate: Number(i.rebar_rate) || 0,
          laminate_sku: (i.work_type || form.work_type) === 'Print & Lamination' ? (i.laminate_sku || '') : '',
          sqft_rate_override: Number(i.sqft_rate_override) || 0,
        })),
      };
      const result = await signageAPI.calculate(payload);
      setCalcResult(result);
      setNeedsRecalc(false);

      // Auto-update custom_per_pcs_rate for items in per_pcs mode to reflect new calculation
      if (result?.items) {
        setForm(prev => {
          const updatedItems = [...prev.items];
          result.items.forEach((r, i) => {
            if (updatedItems[i]?.pricing_mode === 'per_pcs') {
              updatedItems[i] = { ...updatedItems[i], custom_per_pcs_rate: r.final_per_board };
            }
          });
          return { ...prev, items: updatedItems };
        });
      }
    } catch (err) {
      console.error('Calc error:', err);
      alert('Calculation failed');
    } finally { setCalculating(false); }
  };

  const buildPayload = (statusOverride) => {
    const adj = getAdjustedTotals();

    const signageItems = calcResult ? calcResult.items.map((r, i) => {
      const formItem = form.items[i] || {};
      const isPcs = formItem.pricing_mode === 'per_pcs';
      const isSqm = formItem.pricing_mode === 'sqm' && (r.shape || formItem.shape) === 'Rectangular';
      const pi = adj.perItem[i] || { adjustedRate: 0, net: 0, discType: 'amount', discValue: 0, discAmt: 0 };

      return {
        shape: r.shape, dimensions: r.dimensions, description: formItem.description || '', qty: r.qty, area_sqft: r.area_sqft,
        area_sqm: isSqm ? computeAreaSqm({ shape: r.shape, length_mm: formItem.length_mm, height_mm: formItem.height_mm }) : 0,
        length_mm: Number(formItem.length_mm) || 0,
        height_mm: Number(formItem.height_mm) || 0,
        diameter_mm: Number(formItem.diameter_mm) || 0,
        base_material: formItem.base_material || '',
        retro_sheet: formItem.retro_sheet || '',
        work_type: formItem.work_type || form.work_type || '',
        post_type: formItem.post_type || '',
        post_length_ft: Number(formItem.post_length_ft) || 0,
        num_posts: Number(formItem.num_posts) || 0,
        frame_type: formItem.frame_type || '',
        laminate_sku: r.laminate_sku || '',
        laminate_name: r.laminate_name || '',
        laminate_per_sqft: r.laminate_per_sqft || 0,
        facia_rate: r.facia_rate, post_rate: r.post_rate, post_weight_kg: r.post_weight_kg || 0, frame_rate: r.frame_rate, frame_weight_kg: r.frame_weight_kg || 0,
        board_rate: r.board_rate, installation_rate: r.installation_rate,
        final_per_board: pi.adjustedRate,
        total_amount: pi.net,
        disc_type: pi.discType,
        disc_value: pi.discValue,
        discount_amount: pi.discAmt,
        pricing_mode: isSqm ? 'sqm' : (isPcs ? 'per_pcs' : 'sqft'),
        custom_per_pcs_rate: isPcs ? pi.adjustedRate : 0,
        custom_sqm_rate: isSqm ? (Number(formItem.custom_sqm_rate) || 0) : 0,
        foundation_cost: r.foundation_cost || 0,
        concrete_volume_m3: r.concrete_volume_m3 || 0,
        concrete_cost: r.concrete_cost || 0,
        rebar_cost: r.rebar_cost || 0,
        specific_description: formItem.specific_description || '',
        unit: isSqm ? 'SQM' : 'NOS',
      };
    }) : (editQuotation?.signage_items || []).map((si, i) => ({
      ...si, description: form.items[i]?.description || si.description || '', specific_description: form.items[i]?.specific_description || si.specific_description || '', unit: 'NOS',
    }));

    return {
      customer: form.customer, customer_name: form.customer_name,
      customer_gstin: form.customer_gstin || '', customer_address: form.customer_address || '',
      customer_city: form.customer_city || '', customer_state: form.customer_state || '',
      customer_phone: form.customer_phone || '',
      salesman: form.salesman, salesman_name: form.salesman_name,
      salesman_phone: form.salesman_phone || '',
      branch: form.branch,
      ship_to_name: form.ship_to_name || '', ship_to_address: form.ship_to_address || '',
      ship_to_city: form.ship_to_city || '', ship_to_state: form.ship_to_state || '',
      quotation_type: 'signage',
      items: (form.normal_items || []).filter(it => (it.name || '').trim()).map(it => ({
        product: it.product || '', name: (it.name || '').trim(), description: it.description || '',
        hsn_code: it.hsn_code || '', qty: Number(it.qty) || 0, unit: it.unit || 'PCS',
        rate: Number(it.rate) || 0, discount: 0,
        amount: Math.round((Number(it.qty) || 0) * (Number(it.rate) || 0) * 100) / 100,
      })),
      signage_items: signageItems,
      signage_project: {
        project_name: form.project_name, stretch_no: form.stretch_no,
        work_for: form.work_for, work_type: form.work_type,
        margin_percent: Number(form.margin_percent), overhead_percent: Number(form.overhead_percent),
        transportation_amount: Number(form.transportation_amount),
        post_per_kg_rate: Number(form.post_per_kg_rate),
        frame_per_kg_rate: Number(form.frame_per_kg_rate), installation_per_pcs: Number(form.installation_per_pcs),
      },
      net_total: adj.netTotal,
      discount_type: adj.overallDiscType,
      discount_value: adj.overallDiscValue,
      discount_amount: adj.overallDiscAmt,
      subtotal: adj.subtotal, gst_rate: form.gst_rate, gst_amount: adj.gst_amount, total: adj.total,
      terms: form.terms, valid_until: form.valid_until,
      status: statusOverride || 'Draft',
    };
  };

  const handleCreate = async () => {
    if (submittingRef.current) return; // Sync guard
    if (!form.customer || !form.valid_until || !calcResult) {
      alert('Please select customer, set validity, and calculate costs first'); return;
    }
    submittingRef.current = true;
    try {
      setSubmitting(true);
      if (isEdit) {
        await quotationAPI.update(editQuotation.id, buildPayload(editQuotation.status));
      } else {
        await quotationAPI.create(buildPayload('Draft'));
      }
      onBack();
    } catch (err) {
      console.error('Save error:', err);
      alert(err.response?.data?.detail || 'Failed to save quotation');
    } finally { setSubmitting(false); submittingRef.current = false; }
  };

  const handleSaveDraft = async () => {
    if (submittingRef.current) return; // Sync guard
    if (!form.customer) { alert('Please select a customer'); return; }
    submittingRef.current = true;
    try {
      setSubmitting(true);
      if (isEdit) {
        await quotationAPI.update(editQuotation.id, buildPayload('Draft'));
      } else if (draftId) {
        // Update the auto-saved draft instead of creating a duplicate
        await quotationAPI.update(draftId, buildPayload('Draft'));
      } else {
        const result = await quotationAPI.create(buildPayload('Draft'));
        if (result?.id) setDraftId(result.id);
      }
      onBack();
    } catch (err) {
      console.error('Draft save error:', err);
      alert(err.response?.data?.detail || 'Failed to save draft');
    } finally { setSubmitting(false); submittingRef.current = false; }
  };

  const handleRevise = async () => {
    if (submittingRef.current) return; // Sync guard
    if (!form.customer || !calcResult) { alert('Please calculate costs before creating revision'); return; }
    submittingRef.current = true;
    try {
      setSubmitting(true);
      await quotationAPI.revise(editQuotation.id, buildPayload('Draft'));
      onBack();
    } catch (err) {
      console.error('Revision error:', err);
      alert(err.response?.data?.detail || 'Failed to create revision');
    } finally { setSubmitting(false); submittingRef.current = false; }
  };

  const handleGenerateSignagePDF = () => {
    generateSignagePDFUtil({ form, calcResult, branches, logoBase64 });
  };

  const needsLH = (shape) => ['Rectangular', 'Gantry', 'Hazard'].includes(shape);
  const showFoundation = form.work_for === 'Supply & Installation' || form.work_for === 'Installation Only';

  // Compute adjusted totals — margin & overhead baked into each item's unit price
  const getAdjustedTotals = () => {
    const overallDiscType = form.discount_type || 'amount';
    const overallDiscRaw = Number(form.discount_value) || 0;
    // Normal (non-signage) product lines added within the signage quotation
    const normalItems = (form.normal_items || []).filter(it => (it.name || '').trim());
    const normalItemsTotal = normalItems.reduce((s, it) => s + (Number(it.amount) || 0), 0);
    if (!calcResult) {
      // No signage calc yet — but the quote may still carry normal products
      if (normalItemsTotal <= 0) {
        return { perItem: [], itemsGross: 0, itemDiscTotal: 0, itemsTotal: 0, transportAmt: 0, netTotal: 0, overallDiscType, overallDiscValue: 0, overallDiscAmt: 0, subtotal: 0, gst_amount: 0, total: 0, normalItemsTotal: 0 };
      }
      const nNet = normalItemsTotal;
      let nDisc = overallDiscRaw > 0 ? (overallDiscType === 'percent' ? Math.round(nNet * overallDiscRaw / 100) : Math.round(overallDiscRaw)) : 0;
      nDisc = Math.min(nDisc, nNet);
      const nSub = nNet - nDisc;
      const nGst = Math.round(nSub * form.gst_rate / 100);
      return { perItem: [], itemsGross: 0, itemDiscTotal: 0, itemsTotal: 0, transportAmt: 0, netTotal: nNet, overallDiscType, overallDiscValue: overallDiscRaw, overallDiscAmt: nDisc, subtotal: nSub, gst_amount: nGst, total: nSub + nGst, normalItemsTotal };
    }
    const marginPct = Number(form.margin_percent) || 0;
    const overheadPct = Number(form.overhead_percent) || 0;
    const multiplier = 1 + (marginPct / 100) + (overheadPct / 100);
    const perItem = [];
    let itemsGross = 0, itemDiscTotal = 0, itemsTotal = 0;
    calcResult.items.forEach((item, idx) => {
      const formItem = form.items[idx] || {};
      const isPcs = formItem.pricing_mode === 'per_pcs';
      const isSqmOv = formItem.pricing_mode === 'sqm' && formItem.shape === 'Rectangular' && Number(formItem.custom_sqm_rate) > 0;
      const baseRate = isPcs
        ? (Number(formItem.custom_per_pcs_rate) || item.final_per_board)
        : (isSqmOv ? Math.round(Number(formItem.custom_sqm_rate) * computeAreaSqm(formItem)) : item.final_per_board);
      const adjustedRate = Math.round(baseRate * multiplier);
      const gross = adjustedRate * item.qty;
      const dType = formItem.disc_type || 'amount';
      const dVal = Number(formItem.disc_value) || 0;
      let discAmt = 0;
      if (dVal > 0) discAmt = dType === 'percent' ? Math.round(gross * dVal / 100) : Math.round(dVal);
      discAmt = Math.min(discAmt, gross);
      const net = gross - discAmt;
      perItem.push({ adjustedRate, gross, discType: dType, discValue: dVal, discAmt, net });
      itemsGross += gross; itemDiscTotal += discAmt; itemsTotal += net;
    });
    const transportAmt = Number(form.transportation_amount) || 0;
    const netTotal = itemsTotal + transportAmt + normalItemsTotal;
    let overallDiscAmt = 0;
    if (overallDiscRaw > 0) overallDiscAmt = overallDiscType === 'percent' ? Math.round(netTotal * overallDiscRaw / 100) : Math.round(overallDiscRaw);
    overallDiscAmt = Math.min(overallDiscAmt, netTotal);
    const subtotal = netTotal - overallDiscAmt; // taxable value
    const gst_amount = Math.round(subtotal * form.gst_rate / 100);
    const total = subtotal + gst_amount;
    return { perItem, itemsGross, itemDiscTotal, itemsTotal, transportAmt, netTotal, overallDiscType, overallDiscValue: overallDiscRaw, overallDiscAmt, subtotal, gst_amount, total, normalItemsTotal };
  };

  const addNormalItem = () => setForm(prev => ({ ...prev, normal_items: [...(prev.normal_items || []), { product: '', name: '', description: '', hsn_code: '', qty: 1, unit: 'PCS', rate: 0, amount: 0 }] }));
  const removeNormalItem = (idx) => setForm(prev => ({ ...prev, normal_items: (prev.normal_items || []).filter((_, i) => i !== idx) }));
  const updateNormalItem = (idx, field, value) => setForm(prev => {
    const arr = [...(prev.normal_items || [])];
    const row = { ...arr[idx], [field]: value };
    if (field === 'name') {
      const p = products.find(pr => (pr.name || '').toLowerCase() === (value || '').toLowerCase());
      if (p) {
        row.product = p.id;
        row.hsn_code = p.hsn_code || p.hsn || row.hsn_code;
        row.unit = p.unit || row.unit;
        if (!Number(row.rate)) row.rate = p.selling_price || p.rate || p.mrp || 0;
      }
    }
    row.amount = Math.round((Number(row.qty) || 0) * (Number(row.rate) || 0) * 100) / 100;
    arr[idx] = row;
    return { ...prev, normal_items: arr };
  });

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

  const adjusted = getAdjustedTotals();

  return (
    <div data-testid="signage-quote-builder">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button data-testid="signage-back-btn" onClick={handleBack} className="p-1.5 rounded-lg hover:bg-gray-100"><ArrowLeft className="w-4 h-4 text-gray-500" /></button>
          <div>
            <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>
              {isEdit ? 'Edit Signage Quotation' : 'Signage Quotation Builder'}
            </h2>
            <p className="text-[11px] text-gray-500">
              {isEdit ? (
                <span>
                  {editQuotation.quotation_number && <span className="font-semibold text-blue-700 mr-2">{editQuotation.quotation_number}</span>}
                  {editQuotation.revision > 1 && <span className="bg-amber-100 text-amber-700 text-[9px] px-1.5 py-0.5 rounded font-semibold mr-2">Rev-{editQuotation.revision}</span>}
                  Editing signage quotation
                </span>
              ) : 'Automated cost calculation for road signage boards'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="toggle-internal-costs" onClick={() => setShowInternalCosts(!showInternalCosts)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-lg border transition-all ${showInternalCosts ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
            {showInternalCosts ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            {showInternalCosts ? 'Internal View' : 'Customer View'}
          </button>
          {autoSaveStatus && (
            <span data-testid="auto-save-status" className={`text-[10px] px-2.5 py-1 rounded-full font-medium ${autoSaveStatus === 'saving' ? 'bg-yellow-50 text-yellow-600' : 'bg-green-50 text-green-600'}`}>
              {autoSaveStatus === 'saving' ? 'Saving...' : 'Draft saved'}
            </span>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Project & Customer Info */}
          <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
            <h3 className="text-xs font-bold text-gray-900 mb-2">Project Information</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="relative">
                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Customer *</label>
                <CustomerSearchInput
                  value={form.customer_name || ''}
                  customerId={form.customer || ''}
                  onSelect={async (c) => {
                    const spId = c.salesperson_id || '';
                    const sp = salespersons.find(s => s.id === spId);
                    setForm(prev => ({
                      ...prev,
                      customer: c.id, customer_name: c.name,
                      customer_gstin: c.gstin || '', customer_address: c.address || '',
                      customer_city: c.city || '', customer_state: c.state || '',
                      customer_phone: c.phone || '',
                      ...(spId ? { salesman: spId, salesman_name: sp?.name || c.salesperson_name || '', salesman_phone: sp?.phone || '' } : {}),
                    }));
                    // Fetch saved shipping addresses for this customer
                    try {
                      const addrs = await customerAPI.getShippingAddresses(c.id);
                      setCustomerShipAddresses(Array.isArray(addrs) ? addrs : []);
                    } catch { setCustomerShipAddresses([]); }
                  }}
                  onClear={() => { setForm(prev => ({ ...prev, customer: '', customer_name: '', customer_gstin: '', customer_address: '', customer_city: '', customer_state: '', customer_phone: '' })); setCustomerShipAddresses([]); }}
                  placeholder="Search customer..."
                  testId="signage-customer"
                />
                {form.customer && (form.customer_gstin || form.customer_city) && (
                  <div className="flex items-center gap-3 mt-1 text-[9px] text-gray-500" data-testid="signage-customer-details">
                    {form.customer_gstin && <span className="font-mono">GSTIN: {form.customer_gstin}</span>}
                    {(form.customer_city || form.customer_state) && <span>{[form.customer_city, form.customer_state].filter(Boolean).join(', ')}</span>}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Salesman *</label>
                <select data-testid="signage-salesman" value={form.salesman} onChange={e => {
                  const sp = salespersons.find(x => x.id === e.target.value);
                  setForm(prev => ({ ...prev, salesman: e.target.value, salesman_name: sp?.name || '', salesman_phone: sp?.phone || '' }));
                }} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                  <option value="">Select Salesman</option>
                  {salespersons.map(sp => <option key={sp.id} value={sp.id}>{sp.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Project Name</label>
                <input data-testid="signage-project-name" value={form.project_name} onChange={e => setForm({ ...form, project_name: e.target.value })} placeholder="e.g. NHAI Phase-IV" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Stretch / Location</label>
                <input value={form.stretch_no} onChange={e => setForm({ ...form, stretch_no: e.target.value })} placeholder="e.g. Km 433-515 NH4" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Valid Until *</label>
                <input data-testid="signage-valid-until" type="date" value={form.valid_until} onChange={e => setForm({ ...form, valid_until: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Work For *</label>
                <select data-testid="signage-work-for" value={form.work_for} onChange={e => setForm({ ...form, work_for: e.target.value })} className={`w-full px-3 py-2 text-xs border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200 ${!form.work_for ? 'border-red-300 bg-red-50/30' : 'border-gray-200'}`}>
                  <option value="">Select</option>
                  {WORK_FOR_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Type of Work * <span className="text-[9px] text-gray-400">(default — can override per row)</span></label>
                <select data-testid="signage-work-type" value={form.work_type} onChange={e => setForm({ ...form, work_type: e.target.value })} className={`w-full px-3 py-2 text-xs border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200 ${(!form.work_type && !form.items.every(i => !!i.work_type)) ? 'border-red-300 bg-red-50/30' : 'border-gray-200'}`}>
                  <option value="">Select</option>
                  {(rateCard?.work_types || []).map(w => <option key={w.name} value={w.name}>{w.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Branch *</label>
                <select data-testid="signage-branch" value={form.branch} onChange={e => setForm({ ...form, branch: e.target.value })} className={`w-full px-3 py-2 text-xs border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200 ${!form.branch ? 'border-red-300 bg-red-50/30' : 'border-gray-200'}`}>
                  <option value="">Select</option>
                  {filterBranchesForUser(branches).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">GST Rate (%)</label>
                <select value={form.gst_rate} onChange={e => setForm({ ...form, gst_rate: Number(e.target.value) })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                  {[0, 5, 12, 18, 28].map(r => <option key={r} value={r}>{r}%</option>)}
                </select>
              </div>
            </div>

            {/* Ship To (optional) */}
            <div className="mt-3 border border-dashed border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Ship To (optional — different delivery location)</label>
                {customerShipAddresses.length > 0 && (
                  <select data-testid="signage-ship-to-select"
                    onChange={e => {
                      if (!e.target.value) return;
                      const addr = customerShipAddresses.find(a => a.id === e.target.value || a.label === e.target.value);
                      if (addr) {
                        setForm(prev => ({
                          ...prev,
                          ship_to_name: addr.name || addr.label || '',
                          ship_to_address: addr.address || '',
                          ship_to_city: addr.city || '',
                          ship_to_state: addr.state || '',
                        }));
                      }
                      e.target.value = '';
                    }}
                    className="text-[10px] px-2 py-1 border border-blue-200 rounded-md bg-blue-50 text-blue-700 cursor-pointer focus:outline-none"
                  >
                    <option value="">Select saved address</option>
                    {customerShipAddresses.map((a, i) => (
                      <option key={a.id || i} value={a.id || a.label}>{a.label || a.name || `Address ${i + 1}`} — {a.city || ''}</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Consignee Name</label>
                  <input data-testid="signage-ship-to-name" value={form.ship_to_name || ''} onChange={e => setForm(prev => ({ ...prev, ship_to_name: e.target.value }))} placeholder="Party name" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Address</label>
                  <input data-testid="signage-ship-to-address" value={form.ship_to_address || ''} onChange={e => setForm(prev => ({ ...prev, ship_to_address: e.target.value }))} placeholder="Street address" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5">City</label>
                  <input data-testid="signage-ship-to-city" value={form.ship_to_city || ''} onChange={e => setForm(prev => ({ ...prev, ship_to_city: e.target.value }))} placeholder="City" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5">State</label>
                  <input data-testid="signage-ship-to-state" value={form.ship_to_state || ''} onChange={e => setForm(prev => ({ ...prev, ship_to_state: e.target.value }))} placeholder="State" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
                </div>
              </div>
            </div>
          </div>

          {/* Rates & Overheads */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h3 className="text-xs font-bold text-gray-900 mb-2">Rates & Overheads</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Margin (%)</label>
                <input data-testid="signage-margin" type="number" value={form.margin_percent} onChange={e => setForm(prev => ({ ...prev, margin_percent: e.target.value }))} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" placeholder="0" />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Overhead (%)</label>
                <input data-testid="signage-overhead" type="number" value={form.overhead_percent} onChange={e => setForm(prev => ({ ...prev, overhead_percent: e.target.value }))} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" placeholder="0" />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Transportation (INR)</label>
                <input data-testid="signage-transport" type="number" value={form.transportation_amount} onChange={e => setForm(prev => ({ ...prev, transportation_amount: e.target.value }))} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" placeholder="0" />
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Post Per Kg (INR)</label>
                <input type="number" value={form.post_per_kg_rate} onChange={e => { setForm({ ...form, post_per_kg_rate: e.target.value }); setNeedsRecalc(true); }} placeholder={rateCard?.post_per_kg_rate || '160'} className="w-full px-3 py-2 text-xs border border-amber-200 rounded-lg bg-amber-50/30 focus:outline-none focus:ring-1 focus:ring-amber-300 placeholder:text-amber-400" />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Frame Per Kg (INR)</label>
                <input type="number" value={form.frame_per_kg_rate} onChange={e => { setForm({ ...form, frame_per_kg_rate: e.target.value }); setNeedsRecalc(true); }} placeholder={rateCard?.frame_per_kg_rate || '135'} className="w-full px-3 py-2 text-xs border border-amber-200 rounded-lg bg-amber-50/30 focus:outline-none focus:ring-1 focus:ring-amber-300 placeholder:text-amber-400" />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Installation Per Post (INR)</label>
                <input type="number" value={form.installation_per_pcs} onChange={e => { setForm({ ...form, installation_per_pcs: e.target.value }); setNeedsRecalc(true); }} placeholder={rateCard?.installation_per_pcs || '2200'} className="w-full px-3 py-2 text-xs border border-amber-200 rounded-lg bg-amber-50/30 focus:outline-none focus:ring-1 focus:ring-amber-300 placeholder:text-amber-400" />
              </div>
            </div>
          </div>

          {/* Sign Board Items */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Calculator className="w-4 h-4" style={{ color: '#24549c' }} />
                <h3 className="text-xs font-bold text-gray-900">Sign Board Items</h3>
              </div>
              <button data-testid="signage-add-item" onClick={addItem} className="text-[10px] font-semibold px-2 py-1 rounded-md hover:bg-blue-50" style={{ color: '#24549c' }}>+ Add Board</button>
            </div>

            <div className="space-y-3">
              {form.items.map((item, idx) => (
                <SignageBoardItem
                  key={idx} item={item} idx={idx} form={form} setForm={setForm}
                  calcResult={calcResult} setNeedsRecalc={setNeedsRecalc}
                  updateItem={updateItem} removeItem={removeItem}
                  rateCard={rateCard} getRateFromCard={getRateFromCard}
                  laminates={laminates} showFoundation={showFoundation} showInternalCosts={showInternalCosts}
                  netAmount={adjusted?.perItem?.[idx]?.net}
                />
              ))}
            </div>

            {/* Calculate Button */}
            <div className="flex justify-between items-center mt-4 pt-3 border-t border-gray-100">
              <div className="flex items-center gap-3">
                <button data-testid="signage-calculate-btn" onClick={handleCalculate} disabled={calculating || !form.work_for || (!form.work_type && !form.items.every(i => !!i.work_type)) || !form.branch}
                  className={`flex items-center gap-2 px-5 py-2.5 text-xs font-semibold text-white rounded-lg hover:shadow-lg disabled:opacity-50 transition-all ${needsRecalc ? 'ring-2 ring-amber-400 ring-offset-1' : ''}`} style={{ backgroundColor: '#24549c' }}>
                  {calculating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Calculator className="w-3.5 h-3.5" />}
                  {needsRecalc ? 'Recalculate Costs' : 'Calculate Costs'}
                </button>
                {needsRecalc && calcResult && <span className="text-[10px] text-amber-600 font-medium">Items changed — click to update prices</span>}
              </div>
              {calcResult && (
                <div className="text-right">
                  <div className="text-[10px] text-gray-500">Items Total (incl. margin/OH): <span className="font-semibold">{adjusted.itemsGross.toLocaleString()}</span></div>
                  {adjusted.itemDiscTotal > 0 && <div className="text-[10px] text-rose-600">Item Discounts: <span className="font-semibold">−{adjusted.itemDiscTotal.toLocaleString()}</span></div>}
                  {adjusted.transportAmt > 0 && <div className="text-[10px] text-gray-500">Transportation: <span className="font-semibold">+{adjusted.transportAmt.toLocaleString()}</span></div>}
                  {adjusted.normalItemsTotal > 0 && <div data-testid="signage-additional-products-total" className="text-[10px] text-gray-500">Additional Products: <span className="font-semibold">+{adjusted.normalItemsTotal.toLocaleString()}</span></div>}
                  <div data-testid="signage-net-total" className="text-xs text-gray-500 mt-0.5">Net Total: <span className="font-bold text-gray-900">{adjusted.netTotal.toLocaleString()}</span></div>
                  <div className="flex items-center justify-end gap-1.5 mt-1">
                    <span className="text-[10px] text-gray-500">Discount:</span>
                    <select data-testid="signage-overall-disc-type" value={form.discount_type} onChange={e => setForm(prev => ({ ...prev, discount_type: e.target.value }))}
                      className="px-1.5 py-1 text-[10px] border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-200">
                      <option value="amount">₹</option>
                      <option value="percent">%</option>
                    </select>
                    <input data-testid="signage-overall-disc-value" type="number" min="0" value={form.discount_value}
                      onChange={e => setForm(prev => ({ ...prev, discount_value: e.target.value }))} placeholder="0"
                      className="w-24 px-2 py-1 text-[11px] text-right border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-200" />
                  </div>
                  {adjusted.overallDiscAmt > 0 && (
                    <>
                      <div className="text-[10px] text-rose-600">Discount {form.discount_type === 'percent' ? `(${adjusted.overallDiscValue}%)` : ''}: <span className="font-semibold">−{adjusted.overallDiscAmt.toLocaleString()}</span></div>
                      <div className="text-[10px] text-gray-500">Taxable Value: <span className="font-semibold">{adjusted.subtotal.toLocaleString()}</span></div>
                    </>
                  )}
                  <div data-testid="signage-gst-amount" className="text-xs text-gray-500">GST ({form.gst_rate}%): <span className="font-semibold">{adjusted.gst_amount.toLocaleString()}</span></div>
                  <div data-testid="signage-grand-total" className="text-sm font-bold" style={{ color: '#24549c' }}>Total: INR {adjusted.total.toLocaleString()}</div>
                </div>
              )}
            </div>
          </div>

          {/* Additional (Normal) Products — sold alongside signage in the same quotation */}
          <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="signage-normal-products">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Plus className="w-4 h-4" style={{ color: '#24549c' }} />
                <h3 className="text-xs font-bold text-gray-900">Additional Products <span className="text-[10px] font-normal text-gray-400">(normal items — optional)</span></h3>
              </div>
              <button type="button" data-testid="add-normal-product-btn" onClick={addNormalItem} className="text-[10px] font-semibold px-2 py-1 rounded-md hover:bg-blue-50" style={{ color: '#24549c' }}>+ Add Product</button>
            </div>
            <datalist id="signage-product-options">
              {products.map(p => <option key={p.id} value={p.name} />)}
            </datalist>
            {(form.normal_items || []).length === 0 ? (
              <p className="text-[11px] text-gray-400">No additional products. Click "+ Add Product" to include normal (non-signage) items in this quotation.</p>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-[10px] font-semibold text-gray-500 px-1">
                  <div className="col-span-4">Product</div>
                  <div className="col-span-2">HSN</div>
                  <div className="col-span-1">Qty</div>
                  <div className="col-span-1">Unit</div>
                  <div className="col-span-2">Rate</div>
                  <div className="col-span-1 text-right">Amount</div>
                  <div className="col-span-1"></div>
                </div>
                {(form.normal_items || []).map((it, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`normal-product-row-${idx}`}>
                    <input list="signage-product-options" data-testid={`normal-product-name-${idx}`} value={it.name}
                      onChange={e => updateNormalItem(idx, 'name', e.target.value)} placeholder="Type / select product"
                      className="col-span-4 px-2 py-1.5 text-[11px] border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-200" />
                    <input value={it.hsn_code} onChange={e => updateNormalItem(idx, 'hsn_code', e.target.value)} placeholder="HSN"
                      className="col-span-2 px-2 py-1.5 text-[11px] border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-200" />
                    <input type="number" min="0" value={it.qty} onChange={e => updateNormalItem(idx, 'qty', e.target.value)}
                      className="col-span-1 px-2 py-1.5 text-[11px] text-right border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-200" />
                    <input value={it.unit} onChange={e => updateNormalItem(idx, 'unit', e.target.value)}
                      className="col-span-1 px-2 py-1.5 text-[11px] border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-200" />
                    <input type="number" min="0" value={it.rate} onChange={e => updateNormalItem(idx, 'rate', e.target.value)}
                      className="col-span-2 px-2 py-1.5 text-[11px] text-right border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-200" />
                    <div data-testid={`normal-product-amount-${idx}`} className="col-span-1 text-right text-[11px] font-semibold text-gray-800">{(Number(it.amount) || 0).toLocaleString()}</div>
                    <button type="button" data-testid={`remove-normal-product-${idx}`} onClick={() => removeNormalItem(idx)} className="col-span-1 flex justify-end text-gray-400 hover:text-rose-500"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Terms */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h3 className="text-xs font-bold text-gray-900 mb-3">Terms & Conditions</h3>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(form.terms).map(([key, val]) => {
                if (key === 'freight') {
                  const isCustom = val && !freightOptions.includes(val);
                  return (
                    <div key={key}>
                      <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Freight / Delivery Terms</label>
                      <select value={isCustom ? '__custom__' : val} onChange={e => {
                        if (e.target.value === '__custom__') setForm({ ...form, terms: { ...form.terms, freight: '' } });
                        else setForm({ ...form, terms: { ...form.terms, freight: e.target.value } });
                      }} className="w-full px-2 py-1.5 text-[11px] border border-gray-200 rounded-lg">
                        {freightOptions.map(o => <option key={o} value={o}>{o}</option>)}
                        <option value="__custom__">Custom</option>
                      </select>
                      {isCustom && <input value={val} onChange={e => setForm({ ...form, terms: { ...form.terms, freight: e.target.value } })} placeholder="Custom terms" className="w-full mt-1 px-2 py-1.5 text-[11px] border border-gray-200 rounded-lg" />}
                    </div>
                  );
                }
                return (
                  <div key={key}>
                    <label className="block text-[10px] font-medium text-gray-500 mb-0.5">{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</label>
                    <input value={val} onChange={e => setForm({ ...form, terms: { ...form.terms, [key]: e.target.value } })} className="w-full px-2 py-1.5 text-[11px] border border-gray-200 rounded-lg" />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <button onClick={handleBack} className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
            <button data-testid="signage-save-draft" onClick={handleSaveDraft} disabled={submitting}
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50">
              <Save className="w-3.5 h-3.5" /> Save Draft
            </button>
            {calcResult && (
              <button data-testid="signage-download-pdf" onClick={handleGenerateSignagePDF} className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
                <Download className="w-3.5 h-3.5" /> PDF Preview
              </button>
            )}
            {isEdit && (
              <button data-testid="signage-revise-btn" onClick={handleRevise} disabled={submitting || !calcResult}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg disabled:opacity-50 bg-amber-600">
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
                New Revision
              </button>
            )}
            <button data-testid="signage-submit-btn" onClick={handleCreate} disabled={submitting || !calcResult}
              className="flex items-center gap-2 px-5 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>
              {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {isEdit ? 'Update Quotation' : 'Create Signage Quotation'}
            </button>
          </div>
        </div>

        {/* Right sidebar - Cost Summary */}
        <SignageCostSummary
          calcResult={calcResult} form={form} adjusted={adjusted}
          showInternalCosts={showInternalCosts} rateCard={rateCard}
        />
      </div>
    </div>
  );
};

export default SignageQuotationBuilder;
